import { type IProjectBus, TOKEN_IProjectBus } from "@/services/ProjectBus";

import log from "loglevel";
import { inject, injectable } from "tsyringe";

export const SHORTCUTS = {
    ArrowUp: 'ArrowUp',
    ArrowDown: 'ArrowDown',
    ArrowLeft: 'ArrowLeft',
    ArrowRight: 'ArrowRight',
    Tab: 'Tab',
    Enter: 'Enter',
    Space: ' ',
    F2: 'F2',
    Insert: 'Insert',
    KeyC: 'c',
    KeyU: 'u',
    KeyL: 'l',
    KeyV: 'v',
    KeyZ: 'z',
    KeyY: 'y',
    KeyP: 'p',
    KeyI: 'i',
    KeyR: 'r',
    KeyD: 'd',
    KeyM: 'm',
    Escape: 'Escape',
    ContextMenu: 'ContextMenu',
    Delete: 'Delete'
};

export interface KeyModifiers {
    alt?: boolean;
    ctrl?: boolean;
    meta?: boolean;
    shift?: boolean
}

/**
 * Interface defining methods for keyboard event management service.
 * Handles global keyboard events and publishes them to the ProjectBus.
 * 
 * Provides functionality to:
 * - Listen for keyboard shortcuts and global key events
 * - Filter out events from input elements
 * - Start and stop the keyboard listener
 */
export interface IDomKeyboard {
    /**
     * Register a specific keyboard shortcut with modifiers
     * @param key The key to listen for
     */
    registerShortcut(key: string, modifiers?: KeyModifiers): void;
    registerShortcutSet(keys: string[], modifiers?: KeyModifiers): void;
    initialize(): void;
}

@injectable()
export class DomKeyboard implements IDomKeyboard {
    private boundKeyDownHandler: ((event: KeyboardEvent) => void) | undefined;
    private registeredShortcuts: Array<{
        key: string;
        modifiers?: KeyModifiers;
    }> = [];

    constructor(
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus
    ) {
    }

    initialize = (): void => {
        log.info("DomKeyboard: Initializing keyboard service");

        this.boundKeyDownHandler = this.handleKeyDown.bind(this);
        document.addEventListener('keydown', this.boundKeyDownHandler);

        log.info("DomKeyboard: Registering global shortcuts");
        this.registerShortcut(SHORTCUTS.Escape);
        this.registerShortcut(SHORTCUTS.ContextMenu);
    }

    registerShortcutSet = (
        keys: string[],
        modifiers?: KeyModifiers
    ) => {
        keys.forEach(key => this.registerShortcut(key, modifiers))
    }

    registerShortcut = (
        key: string,
        modifiers?: KeyModifiers
    ): void => {
        this.registeredShortcuts.push({ key, modifiers });
        log.debug(`DomKeyboard: Registered shortcut - ${this.formatShortcut(key, modifiers)}`);
    }

    private formatShortcut(
        key: string,
        modifiers?: KeyModifiers
    ): string {
        const parts = [];
        if (modifiers?.ctrl) parts.push('Ctrl');
        if (modifiers?.alt) parts.push('Alt');
        if (modifiers?.shift) parts.push('Shift');
        if (modifiers?.meta) parts.push('Meta');
        parts.push(key);
        return parts.join('+');
    }

    private handleKeyDown(event: KeyboardEvent): void {
        // Only process if it's a shortcut or a non-input element
        if (!this.isRegisteredShortcut(event) || this.isInputElement(event.target as Element))
            return;

        log.info("Processing keypress for shortcut:", event.key, event.code, event.altKey, event.ctrlKey, event.metaKey, event.shiftKey)

        // Publish the keyboard event to the ProjectBus
        this.projectBus.keyDown(event.key, event.code, event.altKey, event.ctrlKey, event.metaKey, event.shiftKey,
            () => {
                log.debug(`DomKeyboard: Preventing default for key - ${event.key}`);
                event.preventDefault();
            },
            () => {
                log.debug(`DomKeyboard: Stopping propagation for key - ${event.key}`);
                event.stopPropagation();
            }
        );
    }

    private isInputElement(element: Element | null): boolean {
        if (!element) return false;

        const tagName = element.tagName.toLowerCase();
        const isEditable = element.hasAttribute('contenteditable') && element.getAttribute('contenteditable') !== 'false' && element.getAttribute('data-readonly') !== 'true';

        // Check if a button has focus
        const isButtonFocused = tagName === 'button' ||
            (element.getAttribute('role') === 'button') ||
            (element.classList.contains('btn') || element.classList.contains('button'));

        // Detect input elements where typing is expected
        return (
            tagName === 'input' ||
            tagName === 'textarea' ||
            tagName === 'select' ||
            isButtonFocused ||
            isEditable
        );
    }

    private isRegisteredShortcut(event: KeyboardEvent): boolean {
        return this.registeredShortcuts.some(shortcut => {
            const keyMatches = shortcut.key.toLowerCase() === event.key.toLowerCase();
            let modifiersMatch = false;
            if (shortcut.modifiers === undefined) {
                modifiersMatch = !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
            } else {
                modifiersMatch =
                    shortcut.modifiers.alt === true && event.altKey ||
                    shortcut.modifiers.ctrl === true && event.ctrlKey ||
                    shortcut.modifiers.meta === true && event.metaKey ||
                    shortcut.modifiers.shift === true && event.shiftKey;
            }

            return keyMatches && modifiersMatch;
        });
    }
}

export const TOKEN_IDomKeyboard = Symbol('IDomKeyboard');